import {  ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import axios from 'axios';
import { showFeedbackModal } from '../UserFeedbackActions';
import { AppDispatch, RootState } from '../..';
import apis from '../../../utils/apis';
import {
  describeUploadResult,
  runSequentialUpload,
  SequentialUploadProgress,
  SequentialUploadResult,
} from '../../../services/uploads/uploadBatching';
import { showModal } from '../modalActions';
import { NoteItem } from '../../../components/Matters/tabs/Notes';
import {
  PiTagLight,
} from 'react-icons/pi';
import { ShareUser } from '../../../components/shared/ShareAccessManager';
import { DropdownOption } from '../../../components/shared/TailwindComponents/Dropdown';
import { setJurisdictionCasesSupport } from '../../../components/Matters/matterConstants';
import { translate } from '../../../i18n/translate';
import { readErrorMessage, toErrorText } from '../../../utils/errorMessage';
interface FetchMattersParams {
  searchTerm?: string;
  filters?: Record<string, any>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
  startDate?: string | null;
  endDate?: string | null;
}

interface SourceData {
  title: string;
  url?: string;
  snippet?: string;
  content?: string;
}

/**
 * The wire shape POST /matters/add_collaborators actually reads.
 *
 * The backend takes ONE object per collaborator and reads the access level off
 * THAT object (`collab_entry.get('access', 'view')`, normalised to
 * view|edit|admin). There is no top-level permission field on the route — the
 * `Permission` this used to send was read by nobody, which is why every
 * collaborator landed on the default 'view' no matter what the UI said.
 */
export interface MatterCollaboratorInput {
  /** Numeric user id or plaintext user UUID — the route resolves either. */
  id: string | number;
  access: MatterAccessLevel;
  name?: string;
  email?: string;
  initials?: string;
  avatarUrl?: string | null;
  role?: string;
}

export type MatterAccessLevel = 'view' | 'edit' | 'admin';

interface CollaboratorData {
  MatterID: string;
  Collaborators: Array<MatterCollaboratorInput | ShareUser>;
  /** Fallback access for entries that carry none of their own. */
  Permission?: MatterAccessLevel;
  Roles?: string[];
}

/** view|edit|admin are the only levels the backend accepts; anything else is 'view'. */
const toAccessLevel = (value: unknown): MatterAccessLevel =>
  value === 'edit' || value === 'admin' || value === 'view' ? value : 'view';

interface QuestionnaireItem {
  question?: string;
  potential_answer?: string;
  who_answered?: string;
  when_answered?: string;
  status?: 'pending' | 'answered';
  client_assigned?: boolean;
}

const handleApiError = (error: any, dispatch: any, options?: { isNotFound?: boolean; redirectTo?: string }) => {
  // Skip cancelled requests (includes licensing errors converted to Cancel by interceptor)
  if (axios.isCancel(error)) return;

  const response = error.response?.data || {};
  let userMessage = toErrorText(response.error) || toErrorText(response.message) || 'Something went wrong. Please try again.';
  const statusCode = error.response?.status;

  // Special handling for missing matters (404 or validation errors about missing UUID)
  if (options?.isNotFound || statusCode === 404 || (statusCode === 400 && userMessage.toLowerCase().includes('matteruuid'))) {
    dispatch(showFeedbackModal({
      showModal: true,
      message: translate("Matter doesn't exist or has been deleted"),
      subMsg: translate('The matter you are looking for could not be found. It may have been deleted or you may not have access to it.'),
      modalType: 'error',
      duration: 0, // Don't auto-dismiss
      showContactSupport: true,
      redirectTo: options?.redirectTo || '/matters',
    }));
    return;
  }

  dispatch(showFeedbackModal({
    showModal: true,
    message: userMessage,
    modalType: statusCode === 403 ? 'warning' : 'error',
    duration: 5000,
  }));
};

/**
 * The four board columns, and the ONLY values `PATCH /matters/<uuid>/stage`
 * accepts — anything else is a 400. Kept as the client-side default so the
 * board can render before `/matters/stages` answers.
 *
 * NOTE this is NOT the per-category ladder `MatterStageControl` drives
 * (`/dropdown/matter-stages` → numeric `stage_id`, different ladder per matter
 * category). This is the coarse, normalised lifecycle every matter shares,
 * which is what makes a single four-column board possible at all.
 */
export const MATTER_BOARD_STAGES = ['Planned', 'Ongoing', 'Final Stages', 'Completed'] as const;
export type MatterBoardStage = typeof MATTER_BOARD_STAGES[number];

const isBoardStage = (value: any): value is MatterBoardStage =>
  typeof value === 'string' && (MATTER_BOARD_STAGES as readonly string[]).includes(value);

/**
 * Revenue may be absent (the lookup failed) or genuinely 0.0. The two are
 * different facts, so an absent figure becomes `null` — "unknown", rendered as
 * a muted dash — and never collapses into a confident zero.
 */
const parseRevenue = (raw: any): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const transformMatterData = (rawMatter: any) => {
  const matterTitle = rawMatter.title || rawMatter.Title || 'Untitled Matter';
  const matterStage = rawMatter.stage_id || rawMatter.Stage || 'Open';

  return {
    /*
      `board_stage` is deliberately its own field rather than reusing `stage`:
      `stage` has meant `stage_id` here since long before the board existed and
      several callers read it as such.
    */
    board_stage: isBoardStage(rawMatter.stage) ? rawMatter.stage : null,
    revenue: parseRevenue(rawMatter.revenue),
    revenue_currency: rawMatter.revenue_currency || null,
    id: rawMatter.id || rawMatter.MatterID || '',
    uuid: rawMatter.uuid || '',
    title: matterTitle,
    description: rawMatter.description || rawMatter.Description || '',
    status: rawMatter.status_id || rawMatter.Status || 'Open',
    stage: matterStage,
    stage_id: rawMatter.stage_id || null,
    stage_name: rawMatter.stage_name || '',
    status_id: rawMatter.status_id || null,
    status_name: rawMatter.status_name || '',
    type: rawMatter.type_id || rawMatter.Type || rawMatter.type || null,
    jurisdiction: rawMatter.jurisdiction || null,
    priority: rawMatter.priority || 'Normal',
    createdAt: rawMatter.created_at || rawMatter.CreatedAt || new Date().toISOString(),
    updatedAt: rawMatter.updated_at || rawMatter.UpdatedAt || new Date().toISOString(),
    created_at: rawMatter.created_at || rawMatter.CreatedAt || new Date().toISOString(),
    updated_at: rawMatter.updated_at || rawMatter.UpdatedAt || new Date().toISOString(),
    dueDate: rawMatter.due_date || rawMatter.DueDate || null,
    client: rawMatter.client || null,
    clientId: rawMatter.client_id || rawMatter.ClientID || null,
    organizationId: rawMatter.organization_id || rawMatter.OrganizationID || null,
    lastModifiedByUserId: rawMatter.last_modified_by_user_id || rawMatter.LastModifiedByUserID || null,
    assignedUserIds: rawMatter.assigned_user_ids || rawMatter.AssignedUserIDs || [],
    isConfidential: rawMatter.is_confidential || rawMatter.IsConfidential || false,
    isShared: rawMatter.is_shared || rawMatter.IsShared || false,
    sharingSettings: rawMatter.sharing_settings || rawMatter.SharingSettings || null,
    deletedAt: rawMatter.deleted_at || rawMatter.DeletedAt || null,
    version: rawMatter.version || 1,
    MatterID: rawMatter.id || rawMatter.MatterID || '',
    Title: matterTitle,
    MatterName: matterTitle,
    MatterStage: matterStage,
    DocumentCount: rawMatter.document_count || rawMatter.DocumentCount || 0,
    Collaborators: rawMatter.collaborators || rawMatter.Collaborators || [],
    Clients: rawMatter.clients || rawMatter.Clients || [],
    Attorneys: rawMatter.attorneys || rawMatter.Attorneys || [],
    processing_status: rawMatter.processing_status || 'pending',
    processing_correlation_id: rawMatter.processing_correlation_id || null,
    processing_started_at: rawMatter.processing_started_at || null,
    processing_completed_at: rawMatter.processing_completed_at || null,
    is_processing: rawMatter.is_processing || false,
    matter_clients: rawMatter.matter_clients || null,
  };
};

export const fetchMatters = (params: FetchMattersParams = {}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const {
      searchTerm = '',
      filters = {},
      sortBy = '',
      sortOrder = 'asc',
      page = 1,
      page_size = 12,
      startDate = null,
      endDate = null,
    } = params;

    const filtersJson = JSON.stringify(filters);

    try {
      /*
        The active list, and the only list this endpoint serves. Archived
        (= soft-deleted) matters are not a scope of /matters/view — they live
        in the recycle bin and are listed from /recycle-bin, which is what the
        Archive view reads. See `archiveMatter` below.
      */
      const response = await apis.get('/matters/view', {
        params: {
          searchTerm, filters: filtersJson, sortBy, sortOrder,
          page, page_size, startDate, endDate,
        }
      });

      if (response.status === 200) {
        const responseData = response.data;
        const rawMatters = responseData.matters || responseData.data || [];
        const pagination = responseData.pagination || {};
        const currentPage = pagination.page || page;
        const totalCount = pagination.total_count || 0;

        const transformedMatters = rawMatters.map(transformMatterData);

        dispatch({
          type: page === 1 ? 'FETCH_ALL_MATTERS' : 'APPEND_MATTERS',
          payload: transformedMatters
        });
        return { matters: transformedMatters, currentPage, total_matters: totalCount };
      }
      handleApiError(response, dispatch);
      return { matters: [], currentPage: 1, total_matters: 0 };
    } catch (error) {
      handleApiError(error, dispatch);
      return { matters: [], currentPage: 1, total_matters: 0 };
    }
  };
};

export const fetchMatterById = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/get', { MatterUUID: matterUuid });
      if (response.status === 200) {
        return response.data.matter;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const fetchMatterDetails = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    // Validate UUID before making request
    if (!matterUuid || matterUuid.trim() === '' || matterUuid === 'undefined' || matterUuid === 'null') {
      handleApiError(
        { response: { status: 400, data: { error: 'Valid MatterUUID is required' } } },
        dispatch,
        { isNotFound: true, redirectTo: '/matters' }
      );
      return null;
    }

    try {
      const response = await apis.post('/matters/details', { MatterUUID: matterUuid });
      if (response.status === 200) {
        return response.data.data || response.data.details || response.data;
      }
      handleApiError(response, dispatch, { isNotFound: true, redirectTo: '/matters' });
      return null;
    } catch (error: any) {
      const statusCode = error.response?.status;
      const errorData = error.response?.data || {};
      const errorMessage = toErrorText(errorData.error) || toErrorText(errorData.message) || '';

      // Treat 404, 400 with UUID validation errors, and "does not exist" messages as "not found"
      const isNotFound = statusCode === 404 ||
        (statusCode === 400 && errorMessage.toLowerCase().includes('matteruuid')) ||
        (statusCode === 400 && errorMessage.toLowerCase().includes('does not exist'));

      handleApiError(error, dispatch, { isNotFound, redirectTo: '/matters' });
      return null;
    }
  };
};

export const fetchMatterSummary = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/summary', { MatterUUID: matterUuid });
      if (response.status === 200) {
        return response.data.summary;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const fetchMatterSources = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/sources', { MatterUUID: matterUuid });
      if (response.status === 200) {
        const sourceData = response.data.sources || response.data.datasources || {};

        const laws = sourceData.laws?.documents || sourceData.laws || [];
        const precedents = sourceData.precedents?.documents || sourceData.cases || [];
        const preparatoryWorks = sourceData.preparatory_works?.documents || [];
        const regulations = sourceData.regulations?.documents || [];
        const agencyDecisions = sourceData.agency_decisions?.documents || [];
        const userDocuments = sourceData.user_documents?.documents || sourceData.user_sources || [];
        const customDatasources = sourceData.custom_datasources || [];
        const userSources = sourceData.user_sources || [];

        return {
          cases: precedents,
          precedents: precedents,
          laws: laws,
          preparatory_works: preparatoryWorks,
          regulations: regulations,
          agency_decisions: agencyDecisions,
          user_documents: userDocuments,
          custom_datasources: customDatasources,
          user_sources: userSources,
          all: [
            ...precedents,
            ...laws,
            ...preparatoryWorks,
            ...regulations,
            ...agencyDecisions,
            ...userDocuments,
            ...customDatasources,
            ...userSources,
          ]
        };
      }
      handleApiError(response, dispatch);
      return { cases: [], precedents: [], laws: [], preparatory_works: [], regulations: [], agency_decisions: [], user_documents: [], custom_datasources: [], user_sources: [], all: [] };
    } catch (error) {
      handleApiError(error, dispatch);
      return { cases: [], precedents: [], laws: [], preparatory_works: [], regulations: [], agency_decisions: [], user_documents: [], custom_datasources: [], user_sources: [], all: [] };
    }
  };
};

export const fetchQuestionnaire = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/questionnaire', { MatterUUID: matterUuid });
      if (response.status === 200) {
        const questionnaire = response.data.questionnaire;
        if (questionnaire && typeof questionnaire === 'object') {
          if (Array.isArray(questionnaire.questions)) {
            return questionnaire.questions;
          }
          if (Array.isArray(questionnaire.items)) {
            return questionnaire.items;
          }
          if (Array.isArray(questionnaire)) {
            return questionnaire;
          }
        }
        return [];
      }
      handleApiError(response, dispatch);
      return [];
    } catch (error) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};

export const createMatter = (matterData: FormData) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>, getState: () => RootState) => {
    try {
      dispatch({ type: 'PREPARING_MATTER_DOCUMENT', payload: { isPreparingDoc: true } });
      const response = await apis.post('/matters/create', matterData);
      if (response.status === 200 || response.status === 202) {
        const matterResponse = response.data;
        const correlationId = matterResponse.correlation_id;

        if (correlationId) {
          // Socket emit removed - legacy code
        }

        return matterResponse;
      }
      dispatch({ type: 'MATTER_CREATION_FAILED', payload: { error: toErrorText(response.data.error) || toErrorText(response.data.message) } });
      handleApiError(response, dispatch);
      return null;
    } catch (error: any) {
      dispatch({ type: 'MATTER_CREATION_FAILED', payload: { error: error.message || 'Failed to create matter' } });
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const registerMatterCreationListeners = (
  dispatch: AppDispatch,
  finalize?: (matterId: string, suggestedTasks: any[]) => void,
  canceled?: (matterId: string) => void
) => {
  // Socket listeners removed - legacy code
};

export const editMatter = (matterUuid: string, updatedData: any, files?: FormData) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/edit', files || { MatterUUID: matterUuid, ...updatedData });
      if (response.status === 200) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: 'Matter updated successfully',
        //   modalType: 'success',
        //   duration: 3000
        // }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * The board's stage columns, straight from the backend (`board_stages`).
 *
 * Falls back to `MATTER_BOARD_STAGES` so the board still renders against a
 * backend that predates the endpoint — the four values are fixed by contract,
 * the call exists so a later fifth column doesn't need a frontend release.
 */
export const fetchMatterBoardStages = () => {
  return async (_dispatch: ThunkDispatch<RootState, undefined, AnyAction>): Promise<string[]> => {
    try {
      const response = await apis.get('/matters/stages');
      const stages = response.data?.board_stages;
      if (Array.isArray(stages) && stages.length > 0) {
        return stages.filter((stage: any) => typeof stage === 'string');
      }
    } catch {
      /* Non-fatal: the caller keeps the contract's four columns. */
    }
    return [...MATTER_BOARD_STAGES];
  };
};

/**
 * Move a matter to a board stage. Returns truthy on success — the board reads
 * that to decide whether to keep the optimistic move or snap the card back.
 *
 * Deliberately quiet on success: a drag-and-drop that already moved the card
 * doesn't need a toast telling the user what they just watched happen. Only
 * the failure is announced, via the shared error handler.
 */
export const updateMatterBoardStage = (matterUuid: string, stage: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.patch(`/matters/${matterUuid}/stage`, { stage });
      if (response.status >= 200 && response.status < 300) {
        dispatch({ type: 'UPDATE_MATTER_BOARD_STAGE', payload: { uuid: matterUuid, stage } });
        return response.data ?? true;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Archive a matter — the SOFT delete, and the only destructive action the
 * normal matter UI offers.
 *
 * "Archive" is WORDING, not a separate mechanism: it is the existing
 * soft-delete, `POST /matters/delete`, which sets `deleted_at` and moves the
 * matter to the recycle bin with its documents, tasks, time entries and audit
 * trail intact. There is no `/matters/archive` endpoint — calling one 404s.
 * The Archive view is the recycle bin filtered to matters, and restore /
 * permanent delete happen through `/recycle-bin/*`.
 *
 * The cascade flags stay off: archiving must not take a matter's tasks or
 * files down with it, since it is meant to be undone.
 *
 * Not to be confused with moving a matter to an "Archived" STATUS, which is a
 * position on the org's own status ladder and leaves the matter active.
 */
export const archiveMatter = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/delete', {
        MatterUUID: matterUuid,
        DeleteTasks: false,
        DeleteFilesFolders: false,
      });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Matter archived'),
          subMsg: translate('You can restore it from the Archive.'),
          modalType: 'success',
          duration: 4000,
        }));
        return response.data ?? true;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Return an archived matter to the active list — the undo of `archiveMatter`.
 *
 * Archiving is the recycle-bin soft delete, so un-archiving is the recycle
 * bin's own restore (`POST /recycle-bin/restore`). There is no
 * `/matters/unarchive` endpoint.
 */
export const unarchiveMatter = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/recycle-bin/restore', {
        items: [{ entity_type: 'matter', id: matterUuid }],
      });
      if (response.status === 200 || response.status === 207) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Matter restored'),
          modalType: 'success',
          duration: 3000,
        }));
        return response.data ?? true;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * The raw soft delete, with its cascade options — used when a matter is being
 * discarded outright (an abandoned draft in the create form), where taking the
 * tasks and files with it is the point.
 *
 * Same endpoint as `archiveMatter`; prefer that one for the user-facing
 * "Archive" action so the cascade stays off. Permanent destruction is not here
 * — it is `permanentDeleteRecycleBinItems`, from the Archive view.
 */
export const deleteMatter = (matterUuid: string, options?: { deleteTasks?: boolean; deleteFilesFolders?: boolean }) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/delete', {
        MatterUUID: matterUuid,
        DeleteTasks: options?.deleteTasks ?? false,
        DeleteFilesFolders: options?.deleteFilesFolders ?? false,
      });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Matter deleted successfully'),
          modalType: 'success',
          duration: 3000
        }));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const deleteBulkMatters = (matterUuids: string[], options?: { deleteTasks?: boolean; deleteFilesFolders?: boolean }) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      if (!Array.isArray(matterUuids) || matterUuids.length === 0) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('No matters selected for deletion'),
          modalType: 'error',
          duration: 3000
        }));
        return null;
      }

      const response = await apis.post('/matters/delete_bulk', {
        MatterUUIDs: matterUuids,
        DeleteTasks: options?.deleteTasks ?? false,
        DeleteFilesFolders: options?.deleteFilesFolders ?? false,
      });

      if (response.status === 200) {
        const { deleted_count, total_count, failed_count } = response.data;

        let message = `${deleted_count} of ${total_count} matter${total_count !== 1 ? 's' : ''} deleted successfully`;
        if (failed_count && failed_count > 0) {
          message += ` (${failed_count} failed)`;
        }

        dispatch(showFeedbackModal({
          showModal: true,
          message: message,
          modalType: failed_count && failed_count > 0 ? 'warning' : 'success',
          duration: 3000
        }));
        return response.data;
      }

      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const getAssignedMatters = (userEmail: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/get_assigned_matters', { UserEmail: userEmail });
      if (response.status === 200) {
        dispatch({ type: 'FETCH_ASSIGNED_MATTERS', payload: response.data.matters });
        return response.data.matters;
      }
      handleApiError(response, dispatch);
      return [];
    } catch (error) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};

/**
 * Add files to a matter from a UI that already has them, with feedback.
 *
 * The thin wrapper over `addFilesToMatter` for the Documents tab and the
 * assistant pane: same batching, plus the toast those two used to get for
 * free. Reports a partial upload rather than claiming success over it.
 */
export const addFileToMatter = (matterUuid: string, files: File[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    const result = await dispatch(addFilesToMatter(matterUuid, files) as any) as unknown as SequentialUploadResult;
    const problem = describeUploadResult(result);

    dispatch(showFeedbackModal({
      showModal: true,
      message: problem
        ? translate('{{done}} of {{total}} files added.', { done: result.uploadedCount, total: result.attempted })
        : translate('Files added successfully'),
      subMsg: problem || undefined,
      modalType: problem ? 'warning' : 'success',
      duration: problem ? 8000 : 3000,
    }));

    return result;
  };
};

/**
 * Push documents onto an already-created matter, in bounded sequential batches.
 *
 * This is the post-create half of the metadata-first flow: `/matters/create`
 * now goes up with `skip_processing=true` and NO `bulk_files`, and the
 * documents follow through here. One reset batch no longer costs the user the
 * whole matter — it costs those files, by name, with a retry that starts from
 * a matter that already exists.
 *
 * Deliberately quiet: it does not dispatch its own success toast, because the
 * caller is the only thing that knows whether a partial upload should still
 * proceed to analysis. It DOES refresh the sources list so whatever landed is
 * on screen.
 */
export const addFilesToMatter = (
  matterUuid: string,
  files: File[],
  onProgress?: (progress: SequentialUploadProgress) => void,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>): Promise<SequentialUploadResult> => {
    const result = await runSequentialUpload(files, {
      send: (batch) => {
        const form = new FormData();
        form.append('MatterUUID', matterUuid);
        batch.forEach((file) => form.append('bulk_files', file));
        return apis.post('/matters/add_file', form);
      },
      onProgress,
    });

    if (result.uploadedCount > 0) {
      dispatch(fetchMatterSources(matterUuid));
    }
    return result;
  };
};

export const analyzeMatter = (matterUuid: string, aiToggles?: Record<string, boolean>, datasourceUuids?: string[], selectedIndexes?: string[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const body: Record<string, any> = {};
      if (aiToggles) {
        body.ai_toggles = {
          fetch_laws: aiToggles.fetchLaws ?? true,
          fetch_precedents: aiToggles.fetchPrecedents ?? true,
          fetch_data_source: aiToggles.fetchDataSource ?? true,
          create_tasks: aiToggles.createTasks ?? true,
          create_questionnaire: aiToggles.createQuestionnaire ?? true,
          generate_description: aiToggles.generateDescription ?? true,
          generate_notes: aiToggles.generateNotes ?? true,
          generate_draft_suggestions: aiToggles.generateDraftSuggestions ?? true,
          generate_insight_suggestions: aiToggles.generateInsightSuggestions ?? true,
        };
      }
      if (datasourceUuids && datasourceUuids.length > 0) {
        body.datasource_uuids = datasourceUuids;
      }
      if (selectedIndexes && selectedIndexes.length > 0) {
        body.selected_indexes = selectedIndexes;
      }
      const response = await apis.post(`/matters/${matterUuid}/analyze`, body);
      // 202 when the engine queues the run, 200 when it answers synchronously.
      if (response.status === 200 || response.status === 202) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Analysis started'),
          modalType: 'success',
          duration: 3000
        }));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const generateMoreForStep = (matterUuid: string, targetStep: string, selectedIndexes?: string[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const body: Record<string, any> = { target_step: targetStep };
      if (selectedIndexes && selectedIndexes.length > 0) {
        body.selected_indexes = selectedIndexes;
      }
      const response = await apis.post(`/matters/${matterUuid}/generate-more`, body);
      if (response.status === 200) {
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const getAnalysisStatus = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/matters/${matterUuid}/analysis-status`);
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };
};

/**
 * Upload a legal source (law/case) file to a matter via /add_file.
 * This uses the complete file pipeline: encrypt → blob storage → FileMetadata record →
 * route to laws.json/precedents.json → queue for AI evaluation → PubSub token.
 *
 * Unlike addSourceToMatter (which only writes metadata), this actually stores the file.
 */
export const uploadLegalSource = (
  matterUuid: string,
  file: File,
  documentType: 'law' | 'case',
  sourceTitle?: string,
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const formData = new FormData();
      formData.append('MatterUUID', matterUuid);
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (sourceTitle) {
        formData.append('source_title', sourceTitle);
      }
      const response = await apis.post('/matters/add_file', formData);
      if (response.status === 200) {
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const addSourceToMatter = (matterUuid: string, sourceType: 'law' | 'case', sourceData: SourceData, file?: File) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const formData = new FormData();
      formData.append('MatterUUID', matterUuid);
      formData.append('source_type', sourceType);
      formData.append('source_data', JSON.stringify(sourceData));
      if (file) {
        formData.append('file', file);
      }
      const response = await apis.post('/matters/add_source', formData);
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Source added successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterSources(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const removeSourceFromMatter = (matterUuid: string, sourceType: string, sourceId: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/remove_source', { MatterUUID: matterUuid, source_type: sourceType, source_id: sourceId });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Source removed successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterSources(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const addMatterCollaborators = (collaboratorData: CollaboratorData) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      /*
        Normalise to the route's contract before sending. Callers hand us
        whatever their own list holds (a `ShareUser`, a `Collaborator` row, a
        bare id), so the resolved identity fields and — crucially — the access
        level are folded onto each collaborator here rather than being left to
        each call site to remember.
      */
      const fallbackAccess = toAccessLevel(collaboratorData.Permission);
      const collaborators = (collaboratorData.Collaborators || []).map((collab: any) => ({
        id: collab.id ?? collab.collaborator_uuid ?? collab.user_id,
        access: toAccessLevel(collab.access ?? collaboratorData.Permission),
        name: collab.name || '',
        email: collab.email || '',
        initials: collab.initials || '',
        avatarUrl: collab.avatarUrl ?? collab.profile_picture ?? null,
        role: (Array.isArray(collab.role) ? collab.role[0] : collab.role) || 'collaborator',
      }));

      const response = await apis.post('/matters/add_collaborators', {
        MatterID: collaboratorData.MatterID,
        Collaborators: collaborators,
        // Kept only so a legacy consumer reading the echo back still sees it;
        // the route itself ignores it.
        Permission: fallbackAccess,
      });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.message || 'Access granted successfully',
          modalType: 'success',
          duration: 3000
        }));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Change a collaborator's access level via the legacy
 * POST /matters/update_collaborator_role route.
 *
 * The route reads `data.get('Access') or data.get('access')` and 400s with
 * "Access level is required" otherwise — it has never read `Role`, which is
 * what this used to send. `CollaboratorID` must be a NUMERIC user id here
 * (the route does `int(collaborator_id)`); callers holding only a user UUID
 * want `updateCollaboratorAccess` and its UUID-native endpoint instead.
 */
export const updateMatterCollaboratorRole = (matterUuid: string, collaboratorId: string | number, access: MatterAccessLevel) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/update_collaborator_role', { MatterUUID: matterUuid, CollaboratorID: collaboratorId, Access: toAccessLevel(access) });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Role updated successfully'),
          modalType: 'success',
          duration: 3000
        }));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const removeMatterCollaborator = (matterUuid: string, collaboratorId: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/remove_collaborator', { MatterUUID: matterUuid, CollaboratorID: collaboratorId });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Access removed successfully'),
          modalType: 'success',
          duration: 3000
        }));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const grantClientAccess = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/grant_client_access', { MatterUUID: matterUuid });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Client access granted successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const addQuestionnaireItem = (matterUuid: string, question: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/add_questionnaire_item', { MatterUUID: matterUuid, question });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Question added successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchQuestionnaire(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const updateQuestionnaireItem = (
  matterUuid: string,
  questionIdentifier: string,
  updatedItem: QuestionnaireItem
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/update_questionnaire_item', {
        MatterUUID: matterUuid,
        question_id: questionIdentifier,
        updated_item: updatedItem
      });

      if (response.status === 200) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: ('Question updated successfully'),
        //   modalType: 'success',
        //   duration: 3000
        // }));
        dispatch(fetchQuestionnaire(matterUuid));
        return response.data;
      }

      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const removeQuestionnaireItem = (matterUuid: string, questionId: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/remove_questionnaire_item', { MatterUUID: matterUuid, question_id: questionId });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Question removed successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchQuestionnaire(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const finalizeQuestionnaireItem = (matterUuid: string, questionId: string, answer?: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/finalize_questionnaire_item', { MatterUUID: matterUuid, question_id: questionId, answer });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Question finalized successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchQuestionnaire(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const assignQuestionnaireItem = (matterUuid: string, questionId: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/assign_questionnaire_item', { MatterUUID: matterUuid, question_id: questionId });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Question assigned to client successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchQuestionnaire(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const restoreQuestionnaireItem = (matterUuid: string, questionId: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/restore_questionnaire_item', {
        MatterUUID: matterUuid,
        question_id: questionId
      });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Question restored successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchQuestionnaire(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const updateMatterStage = (matterUuid: string, newStage: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/update_matter_stage', { MatterUUID: matterUuid, new_stage: newStage });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Matter stage updated successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const updateMatterProcessStatus = (matterUuid: string, processName: string, statusName: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/update_matter_process_status', { MatterUUID: matterUuid, ProcessName: processName, StatusName: statusName });
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Process status updated successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const fetchMatterNotes = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/notes', { MatterUUID: matterUuid });
      if (response.status === 200) {
        const notesData = response.data.notes;
        if (notesData && typeof notesData === 'object') {
          if (Array.isArray(notesData.notes)) {
            return notesData.notes;
          }
          if (Array.isArray(notesData)) {
            return notesData;
          }
        }
        return [];
      }
      handleApiError(response, dispatch);
      return [];
    } catch (error) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};

export const fetchSuggestedTasks = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/suggested_tasks', { MatterUUID: matterUuid });
      if (response.status === 200) {
        const tasks = response.data.suggested_tasks;
        if (Array.isArray(tasks)) {
          return tasks;
        }
        return [];
      }
      handleApiError(response, dispatch);
      return [];
    } catch (error) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};

export const updateSuggestedTasks = (matterUuid: string, tasks: any[]) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/suggested_tasks/update', {
        MatterUUID: matterUuid,
        tasks
      });
      if (response.status === 200) {
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const addNoteToMatter = (matterUuid: string, noteData: { label: string; value: string; type: string }) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/add_note', {
        MatterUUID: matterUuid,
        label: noteData.label,
        value: noteData.value,
        type: noteData.type
      });

      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: ('Note added successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterNotes(matterUuid));
        return response.data;
      }

      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const updateNoteInMatter = (matterUuid: string, noteData: {
  label: string;
  value: string;
  type: string;
  oldLabel: string
}) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/update_note', {
        MatterUUID: matterUuid,
        label: noteData.label,
        value: noteData.value,
        type: noteData.type,
        old_label: noteData.oldLabel
      });

      if (response.status === 200) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: ('Note updated successfully'),
        //   modalType: 'success',
        //   duration: 3000
        // }));
        dispatch(fetchMatterNotes(matterUuid));
        return response.data;
      }

      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const deleteNoteFromMatter = (matterUuid: string, label: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.post('/matters/delete_note', {
        MatterUUID: matterUuid,
        label: label
      });

      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: ('Note deleted successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterNotes(matterUuid));
        return response.data;
      }

      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const showEditMatterDataModal = (
  matterId: string,
  category: 'notes',
  item?: NoteItem,
  onConfirm?: (data: { label: string; value: string; type: string; oldLabel?: string }) => Promise<void>
) => {
  return (dispatch: any) => {
    const operation: 'add' | 'edit' = item ? 'edit' : 'add';
    dispatch(showModal({
      showModal: true,
      type: 'editMatterData',
      icon: <PiTagLight className='w-6 h-6 text-gray-600' />,
      message: operation === 'add' ? ('Add Note') : ('Edit Note'),
      subMsg: operation === 'add' ? ('Add a structured note to organize important information.') : ('Edit your existing note.'),
      item,
      category,
      matterId,
      onConfirm: async (data: { label: string; value: string; type: string }) => {
        if (onConfirm) {
          await onConfirm({ ...data, oldLabel: item?.label });
        }
      },
    }));
  };
};

interface MatterDropdownsResponse {
  languages?: any[];
  jurisdictions?: any[];
  matter_types?: any[];
  matter_statuses?: any[];
  matter_stages?: any[];
  client_roles?: any[];
  collaborator_roles?: any[];
  ai_settings?: any;
}

interface MatterDropdownsData {
  languages: DropdownOption[];
  jurisdictions: DropdownOption[];
  matterTypes: DropdownOption[];
  matterStatuses: DropdownOption[];
  matterStages: DropdownOption[];
  clientRoles: DropdownOption[];
  collaboratorRoles: DropdownOption[];
  aiSettings?: any;
}

export const fetchMatterDropdowns = () => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get('/matters/dropdowns');

      if (response.status === 200 && response.data.data) {
        const dropdownsData: MatterDropdownsResponse = response.data.data;

        // Populate dynamic case-law support from the raw rows (has_cases is
        // dropped by transformDropdownArray, which only keeps value/label).
        setJurisdictionCasesSupport(dropdownsData.jurisdictions as any);

        const transformedData: MatterDropdownsData = {
          languages: transformDropdownArray(dropdownsData.languages || [], 'languages'),
          jurisdictions: transformDropdownArray(dropdownsData.jurisdictions || [], 'jurisdictions'),
          matterTypes: transformDropdownArray(dropdownsData.matter_types || [], 'matter_types'),
          matterStatuses: transformDropdownArray(dropdownsData.matter_statuses || [], 'matter_statuses'),
          matterStages: transformDropdownArray(dropdownsData.matter_stages || [], 'matter_stages'),
          clientRoles: transformDropdownArray(dropdownsData.client_roles || [], 'client_roles'),
          collaboratorRoles: transformDropdownArray(dropdownsData.collaborator_roles || [], 'collaborator_roles'),
          aiSettings: dropdownsData.ai_settings,
        };

        return transformedData;
      }

      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

const transformDropdownArray = (items: any[], type: string): DropdownOption[] => {
  if (!Array.isArray(items)) return [];

  return items.map((item: any) => {
    let value = '';
    let label = '';

    switch (type) {
      case 'matter_types':
      case 'matter_statuses':
      case 'matter_stages':
        value = item.code || item.Code || item.id?.toString() || '';
        label = item.label || item.Label || item.name || '';
        break;

      case 'client_roles':
        value = String(item.MatterRoleID || item.role_id || item.id || item.Label || '');
        label = item.Label || item.label || item.name || '';
        break;

      case 'collaborator_roles':
        value = item.Label || item.label || item.name || '';
        label = item.Label || item.label || item.name || '';
        break;

      case 'languages':
        value = item.code || item.Code || ''; // Use code as the value (e.g., "en", "se")
        label = item.name || item.label || item.Label || ''; // Use name as the display label
        break;

      case 'jurisdictions':
        value = item.code || item.Code || ''; // Use code as the value (e.g., "SE", "DE")
        label = item.name || item.label || item.Label || ''; // Use name as the display label
        break;

      default:
        if ((item.code || item.Code) && (item.label || item.Label)) {
          value = item.code || item.Code || '';
          label = item.label || item.Label || '';
        } else if ((item.Code || item.code) && (item.Label || item.label)) {
          value = item.Code || item.code || '';
          label = item.Label || item.label || '';
        } else if (item.name && item.label) {
          value = item.name;
          label = item.label;
        } else if (item.name) {
          value = item.name;
          label = item.name;
        } else if (item.Value && item.DisplayName) {
          value = item.Value;
          label = item.DisplayName;
        } else {
          const keys = Object.keys(item);
          value = item[keys[0]] || '';
          label = item[keys[1] || keys[0]] || '';
        }
        break;
    }

    return {
      value: String(value),
      label: String(label),
      active: item.active !== undefined ? item.active : true,
    };
  });
};

export const setPrefetchedMatterData = (matterData: any, tasks: any[] = []) => {
  return (dispatch: any) => {
    dispatch({
      type: 'SET_PREFETCHED_MATTER_DATA',
      payload: { data: matterData, tasks },
    });
  };
};

export const setCurrentMatter = (matterData: any) => {
  return (dispatch: any) => {
    dispatch({
      type: 'SET_CURRENT_MATTER',
      payload: matterData,
    });
  };
};

export const setLoadingCurrentMatter = (isLoading: boolean) => {
  return (dispatch: any) => {
    dispatch({
      type: 'SET_LOADING_CURRENT_MATTER',
      payload: isLoading,
    });
  };
};

export const clearPrefetchedMatterData = () => {
  return (dispatch: any) => {
    dispatch({
      type: 'CLEAR_PREFETCHED_MATTER_DATA',
    });
  };
};

export const clearCurrentMatter = () => {
  return (dispatch: any) => {
    dispatch({
      type: 'CLEAR_CURRENT_MATTER',
    });
  };
};

export const prefetchMatterDataAndTasks = (matterUuid: string) => {
  return async (dispatch: any) => {
    try {
      dispatch(setLoadingCurrentMatter(true));

      const matterDetails = await dispatch(fetchMatterDetails(matterUuid));

      if (matterDetails) {
        dispatch(setPrefetchedMatterData(matterDetails, []));
      }

      dispatch(setLoadingCurrentMatter(false));
      return { matterDetails };
    } catch (error) {
      console.error('Error prefetching matter data:', error);
      dispatch(setLoadingCurrentMatter(false));
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Stream a file from blob_path using the stream-file endpoint
 * Handles decryption automatically on backend with fallback to raw streaming
 * Returns a blob that can be displayed inline in the reader component
 */
export const streamMatterSource = (
  blobPath: string,
  disposition: 'inline' | 'attachment' = 'inline',
  contentType?: string,
  /**
   * The container the blob lives in. A matter's own files are in the default
   * one, so this was never sent — but a DATASOURCE citation names
   * `service-providers`, and without it the endpoint looks in the wrong place
   * and answers 400 blob-not-found. Only sent when the citation names one.
   */
  container?: string
) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {

    try {
      const payload: any = {
        blob_path: blobPath,
        Disposition: disposition
      };

      if (container) {
        payload.container = container;
      }

      if (contentType) {
        payload.ContentType = contentType;
      }

      const response = await apis({
        method: 'POST',
        url: '/matters/stream-source',
        data: payload,
        responseType: 'blob'
      });


      if (!response) {
        console.error('[streamMatterSource] No response received from server');
        return {
          success: false,
          error: 'No response from server'
        };
      }

      if (response.status !== 200) {
        console.error('[streamMatterSource] Unexpected status code', {
          status: response.status,
          statusText: response.statusText
        });
        return {
          success: false,
          error: `Server returned status ${response.status}`
        };
      }

      const blob = response.data;
      if (!blob || !(blob instanceof Blob)) {
        console.error('[streamMatterSource] Invalid blob received', {
          blob_exists: !!blob,
          is_blob_instance: blob instanceof Blob
        });
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      if (blob.size === 0) {
        console.warn('[streamMatterSource] Received empty blob (0 bytes)', {
          blobPath,
          blob_type: blob.type
        });
        return {
          success: false,
          error: 'File is empty or could not be retrieved'
        };
      }

      // Only reject text/html or empty type if the source is NOT an HTML file
      // Legal data sources (e.g. Swedish lagen.nu) are legitimate HTML files
      const isHtmlSource = /\.html?$/i.test(blobPath);
      if (!isHtmlSource && (blob.type === 'text/html' || blob.type === '')) {
        console.warn('[streamMatterSource] Received HTML instead of file content', {
          blob_type: blob.type,
          blob_size: blob.size,
          blobPath
        });
        return {
          success: false,
          error: 'Server returned an error. File may not exist, is encrypted, or access was denied. Check server logs for details.'
        };
      }

      const detectedContentType = ((response.headers['content-type'] as string) || '').toLowerCase().trim() || contentType || 'application/octet-stream';

      return {
        success: true,
        blob,
        contentType: detectedContentType,
        blobObject: blob
      };
    } catch (error: any) {
      console.error('[streamMatterSource] Caught exception', {
        error_message: error.message,
        error_code: error.code,
        blobPath
      });
      const errorMsg = readErrorMessage(error, 'Error streaming matter source');
      return {
        success: false,
        error: errorMsg
      };
    }
  };

/**
 * Stream an encrypted document from a matter using the stream-document endpoint
 * Extracts file_uuid from blob_path and calls the document-specific streaming API
 * Handles decryption automatically on backend and returns a blob for the reader component
 */
export const streamDocument = (
  matterUuid: string,
  blobPath: string,
  disposition: 'inline' | 'attachment' = 'inline',
  contentType?: string
) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      // Extract file_uuid from blob_path (the last segment)
      const fileUuid = blobPath.split('/').pop();

      if (!fileUuid) {
        console.error('[streamDocument] Could not extract file_uuid from blob_path:', blobPath);
        return {
          success: false,
          error: 'Invalid file path'
        };
      }

      const payload = {
        matter_uuid: matterUuid,
        file_uuid: fileUuid,
        disposition: disposition
      };

      const response = await apis({
        method: 'POST',
        url: '/matters/stream-document',
        data: payload,
        responseType: 'blob'
      });

      if (!response) {
        console.error('[streamDocument] No response received from server');
        return {
          success: false,
          error: 'No response from server'
        };
      }

      if (response.status !== 200) {
        console.error('[streamDocument] Unexpected status code', {
          status: response.status,
          statusText: response.statusText,
          matterUuid,
          fileUuid
        });
        return {
          success: false,
          error: `Server returned status ${response.status}`
        };
      }

      const blob = response.data;
      if (!blob || !(blob instanceof Blob)) {
        console.error('[streamDocument] Invalid blob received', {
          blob_exists: !!blob,
          is_blob_instance: blob instanceof Blob
        });
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      if (blob.size === 0) {
        console.warn('[streamDocument] Received empty blob (0 bytes)', {
          matterUuid,
          fileUuid,
          blob_type: blob.type
        });
        return {
          success: false,
          error: 'File is empty or could not be retrieved'
        };
      }

      if (blob.type === 'text/html' || blob.type === '') {
        console.warn('[streamDocument] Received HTML instead of file content', {
          blob_type: blob.type,
          blob_size: blob.size,
          matterUuid,
          fileUuid
        });
        return {
          success: false,
          error: 'Server returned an error. File may not exist, is encrypted, or access was denied. Check server logs for details.'
        };
      }

      const detectedContentType = ((response.headers['content-type'] as string) || '').toLowerCase().trim() || contentType || 'application/octet-stream';

      return {
        success: true,
        blob,
        contentType: detectedContentType,
        blobObject: blob
      };
    } catch (error: any) {
      console.error('[streamDocument] Caught exception', {
        error_message: error.message,
        error_code: error.code,
        matterUuid,
        blobPath
      });
      const errorMsg = readErrorMessage(error, 'Error streaming document');
      return {
        success: false,
        error: errorMsg
      };
    }
  };

/**
 * Download an encrypted document from a matter using the download-document endpoint
 * Triggers a browser download of the decrypted document
 */
export const downloadDocument = (
  matterUuid: string,
  blobPath: string,
  filename: string = 'document'
) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      // Extract file_uuid from blob_path (the last segment)
      const fileUuid = blobPath.split('/').pop();

      if (!fileUuid) {
        console.error('[downloadDocument] Could not extract file_uuid from blob_path:', blobPath);
        return {
          success: false,
          error: 'Invalid file path'
        };
      }

      const payload = {
        matter_uuid: matterUuid,
        file_uuid: fileUuid
      };

      const response = await apis({
        method: 'POST',
        url: '/matters/download-document',
        data: payload,
        responseType: 'blob'
      });

      if (!response || response.status !== 200) {
        console.error('[downloadDocument] Unexpected status code', {
          status: response?.status,
          matterUuid,
          fileUuid
        });
        return {
          success: false,
          error: `Server returned status ${response?.status || 'unknown'}`
        };
      }

      const blob = response.data;
      if (!blob || !(blob instanceof Blob)) {
        console.error('[downloadDocument] Invalid blob received', {
          blob_exists: !!blob,
          is_blob_instance: blob instanceof Blob
        });
        return {
          success: false,
          error: 'Invalid file content received from server'
        };
      }

      if (blob.size === 0) {
        console.warn('[downloadDocument] Received empty blob');
        return {
          success: false,
          error: 'File is empty'
        };
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.info('[downloadDocument] Document downloaded successfully', {
        matterUuid,
        fileUuid,
        filename,
        blob_size: blob.size
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: translate('Document downloaded successfully'),
        modalType: 'success',
        duration: 3000
      }));

      return {
        success: true,
        message: translate('Document downloaded successfully')
      };
    } catch (error: any) {
      console.error('[downloadDocument] Caught exception', {
        error_message: error.message,
        matterUuid,
        blobPath
      });
      const errorMsg = readErrorMessage(error, 'Error downloading document');
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 5000
      }));
      return {
        success: false,
        error: errorMsg
      };
    }
  };

/**
 * Delete an encrypted document from a matter using the delete-document endpoint
 * Removes the document from the matter's storage and metadata
 */
export const deleteDocument = (
  matterUuid: string,
  blobPath: string
) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      // Extract file_uuid from blob_path (the last segment)
      const fileUuid = blobPath.split('/').pop();

      if (!fileUuid) {
        console.error('[deleteDocument] Could not extract file_uuid from blob_path:', blobPath);
        return {
          success: false,
          error: 'Invalid file path'
        };
      }

      const payload = {
        matter_uuid: matterUuid,
        file_uuid: fileUuid
      };

      const response = await apis({
        method: 'POST',
        url: '/matters/delete-document',
        data: payload
      });

      if (!response || response.status !== 200) {
        console.error('[deleteDocument] Unexpected status code', {
          status: response?.status,
          matterUuid,
          fileUuid
        });
        return {
          success: false,
          error: `Server returned status ${response?.status || 'unknown'}`
        };
      }

      const result = response.data;
      if (!result.success) {
        console.error('[deleteDocument] Server error', {
          error: result.error,
          matterUuid,
          fileUuid
        });
        return {
          success: false,
          error: toErrorText(result.error) || 'Failed to delete document'
        };
      }

      console.info('[deleteDocument] Document deleted successfully', {
        matterUuid,
        fileUuid,
        filename: result.deleted_file?.filename
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: result.message || 'Document deleted successfully',
        modalType: 'success',
        duration: 3000
      }));

      return {
        success: true,
        message: result.message,
        deletedFile: result.deleted_file
      };
    } catch (error: any) {
      console.error('[deleteDocument] Caught exception', {
        error_message: error.message,
        matterUuid,
        blobPath
      });
      const errorMsg = readErrorMessage(error, 'Error deleting document');
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 5000
      }));
      return {
        success: false,
        error: errorMsg
      };
    }
  };

export interface MatterStage {
  stage_id?: number;
  id?: number;
  name: string;
  sort_order?: number;
  description?: string;
}

export interface MatterStagesResponse {
  current_stage_id: number;
  stages: MatterStage[];
}

export const fetchMatterStages = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/matters/${matterUuid}/stages`);
      if (response.status === 200) {
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

export const fetchMatterDropdownStages = (categoryId?: number | string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const params: any = { active_only: true };
      if (categoryId !== undefined && categoryId !== null) {
        params.category_id = categoryId;
      }
      const response = await apis.get('/dropdown/matter-stages', { params });
      if (response.status === 200) {
        return response.data.data || [];
      }
      handleApiError(response, dispatch);
      return [];
    } catch (error) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};

export const fetchMatterDropdownStatuses = () => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get('/dropdown/matter-statuses', {
        params: { active_only: true }
      });
      if (response.status === 200) {
        return response.data.data || [];
      }
      handleApiError(response, dispatch);
      return [];
    } catch (error) {
      handleApiError(error, dispatch);
      return [];
    }
  };
};

export const advanceMatterStage = (
  matterUuid: string,
  stageId: number,
  statusId?: number
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload: any = {
        stage_id: stageId,
      };

      if (statusId !== undefined) {
        payload.status_id = statusId;
      }

      const response = await apis.patch(`/matters/${matterUuid}/stage`, payload);
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Matter stage updated successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Update collaborator access level for a matter
 * Uses PATCH /matters/collaborators/update-access endpoint
 */
export const updateCollaboratorAccess = (
  matterUuid: string,
  collaboratorUuid: string,
  access: 'view' | 'edit' | 'admin'
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload = {
        matter_uuid: matterUuid,
        collaborator_uuid: collaboratorUuid,
        access: access,
      };

      const response = await apis.patch(`/matters/collaborators/update-access`, payload);
      if (response.status === 200) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: `Collaborator access updated to ${access}`,
        //   modalType: 'success',
        //   duration: 3000
        // }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Get all clients associated with a matter
 * Uses POST /matters/clients/list endpoint
 */
export const getMatterClients = (matterUuid: string) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload = {
        matter_uuid: matterUuid,
      };

      const response = await apis.post(`/matters/clients/list`, payload);
      if (response.status === 200) {
        return response.data?.data || response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch matter clients:', error);
      return null;
    }
  };
};

/**
 * Add a client to a matter
 * Uses POST /matters/clients/add endpoint
 */
export const addClientToMatter = (
  matterUuid: string,
  clientUuid: string,
  clientRole: string = 'primary'
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload = {
        matter_uuid: matterUuid,
        client_uuid: clientUuid,
        client_role: clientRole,
      };

      const response = await apis.post(`/matters/clients/add`, payload);
      if (response.status === 200 || response.status === 201) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Client added to matter successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data?.data || response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Update the role of a client on a matter
 * Uses POST /matters/clients/update-role endpoint
 */
export const updateClientRole = (
  matterUuid: string,
  clientUuid: string,
  newRole: string
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload = {
        matter_uuid: matterUuid,
        client_uuid: clientUuid,
        new_role: newRole,
      };

      const response = await apis.post(`/matters/clients/update-role`, payload);
      if (response.status === 200) {
        // dispatch(showFeedbackModal({
        //   showModal: true,
        //   message: `Client role updated to ${newRole}`,
        //   modalType: 'success',
        //   duration: 3000
        // }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data?.data || response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};

/**
 * Remove a client from a matter
 * Uses POST /matters/clients/remove endpoint
 */
export const removeClientFromMatter = (
  matterUuid: string,
  clientUuid: string
) => {
  return async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const payload = {
        matter_uuid: matterUuid,
        client_uuid: clientUuid,
      };

      const response = await apis.post(`/matters/clients/remove`, payload);
      if (response.status === 200) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Client removed from matter successfully'),
          modalType: 'success',
          duration: 3000
        }));
        dispatch(fetchMatterDetails(matterUuid));
        return response.data?.data || response.data;
      }
      handleApiError(response, dispatch);
      return null;
    } catch (error) {
      handleApiError(error, dispatch);
      return null;
    }
  };
};


// ── AUDIT LOG ────────────────────────────────────────────────────────

export const fetchMatterAuditLog = (matterUuid: string, page: number = 1, limit: number = 50) => {
  return async (_dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis.get(`/matters/${matterUuid}/audit-log`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch matter audit log:', error);
      return { entries: [], pagination: { has_next: false, total_items: 0 } };
    }
  };
};